<script>
import AawzMarkdownRender from '@modules/global/components/aawz-markdown-render/aawz-markdown-render.vue'
import MarkdownFieldWidgets from '@modules/global/components/markdown-field-widgets/markdown-field-widgets.vue'

export default {
  name: 'MarkdownFieldDialog',
  components: {
    AawzMarkdownRender,
    MarkdownFieldWidgets,
  },
  inheritAttrs: false,
  props: {
    value: {
      type: Boolean,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    tablePicker: {
      type: Object,
      default: () => ({}),
    },
    tablePickerLabel: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    isEmpty: {
      type: Boolean,
      default: false,
    },
    previewValue: {
      type: String,
      default: '',
    },
    previewOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPreviewOpen: true,
    }
  },
  computed: {
    dialogId() {
      return 'aawz-markdown-field-dialog-' + this._uid
    },
    dialogMaxWidth() {
      return this.isPreviewOpen ? '1360' : '1024'
    },
  },
  methods: {
    getEditor() {
      return this.$refs.expandedEditor
    },
    handleToolbarAction(item) {
      this.$emit('toolbar-action', item)
    },
    handleTableOpenExpanded(value) {
      this.$emit('table-open-expanded', value)
    },
    handleTableHover(row, col) {
      this.$emit('table-hover', row, col)
    },
    handleTableClear() {
      this.$emit('table-clear')
    },
    handleTableApply(row, col) {
      this.$emit('table-apply', row, col)
    },
    handleEditorInput() {
      this.$emit('editor-input')
    },
    handleEditorFocus() {
      this.$emit('editor-focus')
    },
    handleEditorBlur() {
      this.$emit('editor-blur')
    },
    handleEditorKeydown(event) {
      this.$emit('editor-keydown', event)
    },
    handleEditorMouseup() {
      this.$emit('editor-mouseup')
    },
    handleEditorKeyup() {
      this.$emit('editor-keyup')
    },
    handleEditorPaste(event) {
      this.$emit('editor-paste', event)
    },
    handleClose() {
      this.$emit('close')
    },
    handleSend() {
      if (!this.canEdit) return
      const editor = this.$refs.expandedEditor
      const text = editor ? editor.innerText || '' : ''
      this.$emit('save', text)
      this.handleClose()
    },
    handleInput(value) {
      this.$emit('input', value)
    },
    togglePreview() {
      this.isPreviewOpen = !this.isPreviewOpen
    },
  },
}
</script>

<template>
  <v-dialog
    :value="value"
    content-class="aawz-markdown-field__dialog"
    :max-width="dialogMaxWidth"
    :retain-focus="false"
    persistent
    scrollable
    @input="handleInput"
  >
    <v-card
      class="aawz-markdown-field__dialog-card"
      :class="{ 'is-preview-open': isPreviewOpen }"
    >
      <div class="aawz-markdown-field__dialog-body">
        <div
          class="aawz-markdown-field aawz-markdown-field--dialog"
          :class="{ 'is-preview-only': previewOnly }"
        >
          <div class="aawz-markdown-field__toolbar">
            <MarkdownFieldWidgets
              v-if="!previewOnly"
              :can-edit="canEdit"
              :table-picker="tablePicker"
              :table-picker-label="tablePickerLabel"
              :menu-value="tablePicker.openExpanded"
              :menu-attach="`#${dialogId}`"
              @toolbar-action="handleToolbarAction"
              @table-open="handleTableOpenExpanded"
              @table-hover="handleTableHover"
              @table-clear="handleTableClear"
              @table-apply="handleTableApply"
            />

            <div
              v-else
              class="az-text-primary az-size-12 font-medium flex-grow-1 px-1"
            >
              Pré-visualização
            </div>

            <div class="aawz-markdown-field__toolbar-actions">
              <v-btn
                v-if="!previewOnly"
                class="aawz-markdown-field__toolbar-toggle"
                icon
                x-small
                :title="isPreviewOpen ? 'Ocultar preview' : 'Mostrar preview'"
                @mousedown.prevent
                @click="togglePreview"
              >
                <v-icon>
                  {{ isPreviewOpen ? 'mdi-chevron-left' : 'mdi-chevron-right' }}
                </v-icon>
              </v-btn>

              <v-btn
                class="aawz-markdown-field__toolbar-toggle"
                icon
                x-small
                title="Fechar"
                @mousedown.prevent
                @click="handleClose"
              >
                <icon-svg name="close" size="12" color="#8E8E8E" />
              </v-btn>
            </div>
          </div>

          <div
            class="aawz-markdown-field__dialog-content"
            :class="{
              'is-preview-open': isPreviewOpen,
              'is-preview-only': previewOnly,
            }"
          >
            <div
              v-if="!previewOnly"
              ref="expandedEditor"
              class="aawz-markdown-field__editor aawz-markdown-field__editor--expanded new-ux-scroll"
              :class="{ 'has-placeholder': isEmpty, 'is-markdown': true }"
              :contenteditable="canEdit"
              :data-placeholder="placeholder"
              :aria-disabled="!canEdit"
              v-bind="$attrs"
              @input="handleEditorInput"
              @focus="handleEditorFocus"
              @blur="handleEditorBlur"
              @keydown="handleEditorKeydown"
              @mouseup="handleEditorMouseup"
              @keyup="handleEditorKeyup"
              @paste="handleEditorPaste"
            >
            </div>

            <div
              v-if="isPreviewOpen || previewOnly"
              class="aawz-markdown-field__preview markdown-style new-ux-scroll"
            >
              <AawzMarkdownRender :source="previewValue || ''" />
            </div>
          </div>

          <v-btn
            v-if="!previewOnly"
            class="aawz-markdown-field__dialog-send"
            small
            icon
            primary
            title="Enviar"
            :disabled="!canEdit"
            @mousedown.prevent
            @click="handleSend"
          >
            <icon-svg size="30" name="route-square" color="primary" />
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<style lang="sass">
.aawz-markdown-field__editor--expanded
  flex: 1 1 0
  min-width: 0
  min-height: 55vh
  max-height: 70vh
  overflow-y: scroll
  scrollbar-gutter: stable
.aawz-markdown-field__dialog
  display: flex
  align-items: center
  justify-content: center
.aawz-markdown-field__dialog-card
  width: min(1024px, 92vw)
  max-height: 85vh
  display: flex
  flex-direction: column
.aawz-markdown-field__dialog-body
  padding: 0
  display: flex
  flex-direction: column
  gap: 8px
.aawz-markdown-field--dialog
  display: flex
  flex-direction: column
  min-height: 60vh
  position: relative
.aawz-markdown-field__toolbar-actions
  display: flex
  align-items: center
  gap: 6px
.aawz-markdown-field__dialog-content
  display: flex
  flex-direction: column
  gap: 12px
  flex: 1 1 auto
  min-height: 60vh
  &.is-preview-open
    flex-direction: row
    align-items: stretch
.aawz-markdown-field__preview
  flex: 1 1 0
  min-width: 0
  min-height: 55vh
  max-height: 70vh
  padding: 16px
  background: #FFF
  overflow-y: scroll
  scrollbar-gutter: stable
.aawz-markdown-field__dialog-card.is-preview-open
  width: min(1360px, 96vw)
.aawz-markdown-field__dialog
  .aawz-markdown-field__toolbar
    border-color: #E2E3E4 !important
  .aawz-markdown-field__editor
    border-color: #E2E3E4 !important
    box-shadow: none !important
  .aawz-markdown-field__toolbar:hover,
  .aawz-markdown-field__toolbar:focus-within,
  .aawz-markdown-field__editor:hover,
  .aawz-markdown-field__editor:focus,
  .aawz-markdown-field__editor:focus-within
    border-color: #E2E3E4 !important
    box-shadow: none !important
.aawz-markdown-field__dialog-send
  position: absolute
  right: 16px
  bottom: 16px

.aawz-markdown-field__dialog
  .new-ux-scroll
    &::-webkit-scrollbar
      width: 4px !important
      height: 4px !important

.aawz-markdown-field--dialog.is-preview-only
  .aawz-markdown-field__dialog-content
    flex-direction: row
    align-items: stretch

  .aawz-markdown-field__preview
    flex: 1 1 100%
    min-height: 60vh
    max-height: 75vh
</style>
